import React, { FC, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import "react-data-grid/lib/styles.css";
import DataGrid from "react-data-grid";
import { TextField, Button, Grid, Paper, Typography, CircularProgress } from "@mui/material";
import classes from "./combinations.module.css";
import FileButton from "../../components/file-button";
import useCombinationsLogic from "./useCombinationsLogic";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useCombinationsLayout } from "../../hooks";
import { BarHorizontal } from "../../components";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const ResponsiveGridLayout = WidthProvider(Responsive);

const validationSchema = Yup.object().shape({
    numberOfItems: Yup.number().min(1, "At least one item code is required").required("Required"),
    numberOfMarkets: Yup.number().min(1, "At least one market is required").required("Required"),
    numberOfFactories: Yup.number().min(1, "At least one line is required").required("Required"),
    numberOfPreps: Yup.number(),
});

const Combinations: FC = () => {
    const {
        //vars
        columns,
        unmetDemandsColumns,
        //states
        combinations,
        linesUtilGraphData,
        prepsUtilGraphData,
        unmetDemands,
        output1,
        output2,
        output3,
        output4,
        result,
        isFormulationsHidden,

        //loading
        importLoading,
        exportLoading,

        //functions
        generateCombinations,
        generateOutputs,
        onRowsChange,
        handleFileChange,
        setIsFormulationsHidden,
    } = useCombinationsLogic();
    // Define a custom cell editor for numeric input
    // const NumericCellEditor = ({ row, column, onRowChange, onClose }: RenderEditCellProps<Combination>) => {
    //     const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    //         const value = parseInt(e.target.value);
    //         if (!isNaN(value)) {
    //             onRowChange({ ...row, [column.key]: value });
    //         }
    //     };

    //     return (
    //         <TextField
    //             label={null}
    //             margin="none"
    //             type="number"
    //             value={row[column.key as keyof Combination] || ""}
    //             onChange={handleChange}
    //             fullWidth
    //             classes={{
    //                 root: ".textFieldRoot",
    //             }}
    //         />
    //     );
    // };

    const { gridLayoutProps } = useCombinationsLayout();
    const combRows = useMemo(() => Object.values(combinations), [combinations]);

    return (
        <Formik
            initialValues={{
                numberOfItems: 1,
                numberOfMarkets: 1,
                numberOfFactories: 1,
                numberOfPreps: 0,
            }}
            validationSchema={validationSchema}
            onSubmit={generateCombinations}
        >
            {({ handleSubmit }) => (
                <Form>
                    <Grid container spacing={2} alignItems={"flex-end"} style={{ width: "100%" }}>
                        {/* <Grid item xs={12}>
                            <Typography variant="h4">Combinations</Typography>
                        </Grid> */}
                        <Grid item xs={12} sm={3}>
                            <Field
                                type="number"
                                label="Number of Preps"
                                name="numberOfPreps"
                                as={TextField}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Field
                                type="number"
                                label="Number of Lines"
                                name="numberOfFactories"
                                as={TextField}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Field
                                type="number"
                                label="Number of Markets"
                                name="numberOfMarkets"
                                as={TextField}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Field
                                type="number"
                                label="Number of Items"
                                name="numberOfItems"
                                as={TextField}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button variant="contained" color="primary" onClick={() => handleSubmit()}>
                                Generate Variables
                            </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FileButton
                                disabled={importLoading}
                                onChange={handleFileChange}
                                // onClear={handleClearFile}
                                // clearable={field.value !== null}
                                accept="excel"
                            >
                                {importLoading ? (
                                    <CircularProgress style={{ color: "white", width: 25, height: 25 }} />
                                ) : (
                                    "Import Excel"
                                )}
                            </FileButton>
                        </Grid>
                        {/* Display the Combinations Data Grid */}
                        <Grid item xs={12}>
                            <Paper elevation={3}>
                                <Typography variant="h4" align="center">
                                    Combinations Data Grid
                                </Typography>
                                <DataGrid
                                    columns={columns}
                                    className={"rdg-light"} // "rdg-dark"
                                    rows={combRows}
                                    rowKeyGetter={(row) => `${row.variable}-comb-row`}
                                    onRowsChange={onRowsChange}
                                />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} style={{ marginTop: 15 }}>
                            <Button
                                disabled={exportLoading}
                                variant="contained"
                                color="primary"
                                onClick={() => generateOutputs()}
                            >
                                {exportLoading ? (
                                    <CircularProgress style={{ color: "white", width: 25, height: 25 }} />
                                ) : (
                                    "Generate & Export Outputs"
                                )}
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                style={{ marginLeft: 5 }}
                                onClick={() => setIsFormulationsHidden((prev) => !prev)}
                            >
                                {isFormulationsHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </Button>
                        </Grid>
                    </Grid>

                    <ResponsiveGridLayout {...gridLayoutProps}>
                        <div key="rccp">
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    Line Utilization R.C.C.P
                                </Typography>
                                <div className={classes.cardContent}>
                                    <BarHorizontal data={linesUtilGraphData} />
                                </div>
                            </Paper>
                        </div>
                        <div key="prep_rccp">
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    Prep. Utilization
                                </Typography>
                                <div className={classes.cardContent}>
                                    <BarHorizontal data={prepsUtilGraphData} />
                                </div>
                            </Paper>
                        </div>
                        <div key="unmet">
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    Unmet Demands
                                </Typography>
                                <div className={classes.cardContent}>
                                    <DataGrid
                                        columns={unmetDemandsColumns}
                                        className={"rdg-light"} // "rdg-dark"
                                        rows={unmetDemands}
                                        rowKeyGetter={(row) => `${row.market}-${row.item}-unmet-row`}
                                    />
                                </div>
                            </Paper>
                        </div>

                        <div key="maximization" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    1st Output (Maximization)
                                </Typography>
                                <Typography variant="body1" align="center">
                                    {output1}
                                </Typography>
                            </Paper>
                        </div>

                        <div key="demand-constraints" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    2nd Output (Demand Constraints)
                                </Typography>
                                <Typography variant="body1" align="center">
                                    {output2.split("\n").map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            <br />
                                        </React.Fragment>
                                    ))}
                                </Typography>
                            </Paper>
                        </div>
                        <div key="capacity-constraints" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    3rd Output (Capacity Constraints)
                                </Typography>
                                <Typography variant="body1" align="center">
                                    {output3.split("\n").map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            <br />
                                        </React.Fragment>
                                    ))}
                                </Typography>
                            </Paper>
                        </div>
                        <div
                            key="prep-capacity-constraints"
                            style={{ display: isFormulationsHidden ? "none" : "block" }}
                        >
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    4rd Output (Prep. Capacity Constraints)
                                </Typography>
                                <Typography variant="body1" align="center">
                                    {output4.split("\n").map((prep, index) => (
                                        <React.Fragment key={index}>
                                            {prep}
                                            <br />
                                        </React.Fragment>
                                    ))}
                                </Typography>
                            </Paper>
                        </div>
                        <div key="solution" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                            <Paper elevation={3} className={classes.card}>
                                <Typography variant="h4" align="center" className={classes.cardTitle}>
                                    Optimal Solution
                                </Typography>
                                <Typography variant="body1" align="center">
                                    {result.optimal_values ?? ""}
                                    <br />
                                    {result.optimal_value ?? ""}
                                </Typography>
                            </Paper>
                        </div>
                    </ResponsiveGridLayout>
                </Form>
            )}
        </Formik>
    );
};

export default Combinations;
